'use client';
import React, { useState, useEffect } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Sparkles, ArrowRight, ChevronLeft, RefreshCw } from 'lucide-react';
import type { WizardData } from './VisaWizard';

interface Props {
  data: WizardData;
  updateData: (d: Partial<WizardData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const validationResults = [
  {
    id: 'val-passport',
    doc: 'Passport (bio data page)',
    status: 'pass',
    message: 'Valid passport detected. Expiry: March 2029. Sufficient validity (35 months remaining).',
    detail: null,
  },
  {
    id: 'val-bank',
    doc: 'Bank statement',
    status: 'pass',
    message: 'Balance: €4,820. Meets minimum requirement of €3,000. Statement dated 18 Apr 2026.',
    detail: null,
  },
  {
    id: 'val-photo',
    doc: 'Passport photos',
    status: 'warning',
    message: 'Photo background appears slightly off-white.',
    detail: 'German embassy requires a pure white background. Consider retaking against a white wall or using a photo booth. This may cause a minor delay but is unlikely to result in rejection.',
  },
  {
    id: 'val-insurance',
    doc: 'Travel insurance certificate',
    status: 'pass',
    message: 'Coverage: €45,000. Valid 12 Jun – 10 Jul 2026. Covers all Schengen countries.',
    detail: null,
  },
  {
    id: 'val-flight',
    doc: 'Flight itinerary',
    status: 'pass',
    message: 'Round-trip booking confirmed. Departure: 12 Jun 2026. Return: 10 Jul 2026.',
    detail: null,
  },
  {
    id: 'val-hotel',
    doc: 'Hotel confirmation',
    status: 'pass',
    message: '28-night booking at Hotel Berliner Hof confirmed. Dates match flight itinerary.',
    detail: null,
  },
  {
    id: 'val-missing',
    doc: 'Employment letter',
    status: 'missing',
    message: 'Not uploaded.',
    detail: 'An employment letter significantly strengthens your application. Without it, the embassy may request additional evidence of your ties to your home country. Recommended to upload before submission.',
  },
];

const overallScore = {
  score: 87,
  label: 'Strong application',
  color: 'text-green-700',
  bg: 'bg-green-50',
  border: 'border-green-200',
};

export default function Step6Validation({ onNext, onBack }: Props) {
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [validating, setValidating] = useState(0);

  useEffect(() => {
    // TODO: Call /api/documents/validate with uploaded document IDs
    const steps = [0, 1, 2, 3, 4, 5, 6];
    steps.forEach((step, idx) => {
      setTimeout(() => {
        setValidating(step + 1);
        if (idx === steps.length - 1) {
          setTimeout(() => setLoading(false), 500);
        }
      }, idx * 350);
    });
  }, []);

  const passCount = validationResults.filter((r) => r.status === 'pass').length;
  const warnCount = validationResults.filter((r) => r.status === 'warning').length;
  const missingCount = validationResults.filter((r) => r.status === 'missing').length;

  if (loading) {
    return (
      <div className="wizard-card space-y-6 animate-slide-up">
        <div className="text-center space-y-4 py-8">
          <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto">
            <Sparkles size={28} className="text-blue-500 animate-pulse" />
          </div>
          <div className="space-y-1">
            <h2 className="text-lg font-extrabold text-gray-900">AI is validating your documents...</h2>
            <p className="text-sm text-gray-500">Checking quality, completeness, and embassy requirements</p>
          </div>
          <div className="space-y-2 max-w-xs mx-auto text-left">
            {validationResults.slice(0, validating).map((r) => (
              <div key={`validating-${r.id}`} className="flex items-center gap-2 text-xs text-gray-600 animate-fade-in">
                <CheckCircle2 size={13} className="text-green-500 shrink-0" />
                {r.doc} — checked
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-card space-y-6 animate-slide-up">
      {/* Header */}
      <div className="space-y-1">
        <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest">Step 6 of 9</p>
        <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">Validation complete</h2>
      </div>

      {/* Overall score */}
      <div className={`flex items-center gap-4 p-4 rounded-xl border ${overallScore.border} ${overallScore.bg}`}>
        <div className="relative w-16 h-16 shrink-0">
          <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
            <circle cx="32" cy="32" r="28" fill="none" stroke="#dcfce7" strokeWidth="6" />
            <circle
              cx="32" cy="32" r="28" fill="none"
              stroke="#16a34a" strokeWidth="6"
              strokeDasharray={`${(overallScore.score / 100) * 175.9} 175.9`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm font-extrabold text-green-700 tabular-nums">{overallScore.score}</span>
          </div>
        </div>
        <div>
          <p className={`text-base font-extrabold ${overallScore.color}`}>{overallScore.label}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {passCount} passed · {warnCount} warning{warnCount !== 1 ? 's' : ''} · {missingCount} missing
          </p>
          <p className="text-xs text-gray-400 mt-1">Your application is ready to proceed to AI document generation</p>
        </div>
      </div>

      {/* Results list */}
      <div className="space-y-2">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Document validation results</p>
        {validationResults.map((result) => (
          <div key={result.id} className={`border rounded-xl overflow-hidden transition-all duration-200 ${
            result.status === 'pass' ?'border-green-100'
              : result.status === 'warning' ?'border-amber-200' :'border-red-100'
          }`}>
            <button
              onClick={() => result.detail && setExpanded(expanded === result.id ? null : result.id)}
              className={`w-full flex items-start gap-3 p-3.5 text-left transition-colors ${
                result.detail ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'
              }`}
            >
              {result.status === 'pass' ? (
                <CheckCircle2 size={16} className="text-green-500 shrink-0 mt-0.5" />
              ) : result.status === 'warning' ? (
                <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
              ) : (
                <XCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800">{result.doc}</p>
                <p className={`text-xs mt-0.5 leading-relaxed ${
                  result.status === 'pass' ? 'text-green-700' : result.status === 'warning' ? 'text-amber-700' : 'text-red-600'
                }`}>{result.message}</p>
              </div>
              {result.detail && (
                <span className="text-xs text-blue-500 font-semibold shrink-0 mt-0.5">
                  {expanded === result.id ? 'Less' : 'Details'}
                </span>
              )}
            </button>
            {expanded === result.id && result.detail && (
              <div className="px-4 pb-3 bg-amber-50/50 animate-fade-in">
                <p className="text-xs text-amber-800 leading-relaxed pl-7">{result.detail}</p>
                {result.status === 'missing' && (
                  <button className="ml-7 mt-2 text-xs font-semibold text-blue-600 hover:underline flex items-center gap-1">
                    <RefreshCw size={11} />
                    Upload now
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-3 pt-2">
        <button onClick={onBack} className="btn-secondary py-2.5 px-4 text-sm">
          <ChevronLeft size={15} />
          Back
        </button>
        <button onClick={onNext} className="btn-primary flex-1 justify-center py-2.5 text-sm">
          Generate my application package
          <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
}